import { useEffect, useMemo, useState } from "react";
import { useCollection } from "@amzn/awsui-collection-hooks";
import type { OptionDefinition } from "@amzn/awsui-components-core/internal/components/option/interfaces";
import { compareDates } from "../config/searchTableConfig";
import {
  DEFAULT_OPTION,
  GLS,
  GROUPED_MARKETPLACES,
  STAGES,
  getModifiedOptions,
} from "../config/recentTransactionTableConfig";
import { SearchHistoryItem } from "../types";

const STAGE_FILTER_OPTIONS: readonly OptionDefinition[] = [
  DEFAULT_OPTION,
  ...STAGES,
];

const STATUS_FILTER_OPTIONS: readonly OptionDefinition[] = [
  DEFAULT_OPTION,
  { label: "Healthy", value: "healthy" },
  { label: "Unhealthy", value: "unhealthy" },
  { label: "Pending", value: "pending" },
];

const matchesSelectedOption = (selected: OptionDefinition, value?: string) =>
  selected.value === DEFAULT_OPTION.value || value === selected.value;

const extractGlId = (gl?: string) => gl?.match(/\((\d+)\)$/)?.[1] ?? gl;

interface SearchHistoryFilters {
  stage: OptionDefinition;
  gl: OptionDefinition;
  marketplaces: OptionDefinition[];
  status: OptionDefinition;
}

const createDefaultSearchHistoryFilters = (): SearchHistoryFilters => ({
  stage: DEFAULT_OPTION,
  gl: DEFAULT_OPTION,
  marketplaces: [],
  status: DEFAULT_OPTION,
});

export function useSearchHistory() {
  const [allItems, setAllItems] = useState<SearchHistoryItem[]>([]);
  const [filters, setFilters] = useState<SearchHistoryFilters>(
    createDefaultSearchHistoryFilters
  );

  useEffect(() => {
    const storedOrderIds = JSON.parse(
      localStorage.getItem("orderIds") || "[]"
    ) as SearchHistoryItem[];
    setAllItems(storedOrderIds);
  }, []);

  const marketplaceOptions = useMemo(
    () => getModifiedOptions(GROUPED_MARKETPLACES, filters.marketplaces),
    [filters.marketplaces]
  );

  const filteredHistoryItems = useMemo(
    () =>
      allItems.filter(
        (item) =>
          matchesSelectedOption(filters.stage, item.stage) &&
          matchesSelectedOption(filters.gl, extractGlId(item.glProductGroup)) &&
          (filters.marketplaces.length === 0 ||
            filters.marketplaces.some(
              (marketplace) =>
                marketplace.value === DEFAULT_OPTION.value ||
                marketplace.value === item.countryCode
            )) &&
          matchesSelectedOption(filters.status, item.status)
      ),
    [allItems, filters]
  );

  const { items, actions, collectionProps, paginationProps } = useCollection(
    filteredHistoryItems,
    {
      pagination: { pageSize: 15 },
      sorting: {
        defaultState: {
          sortingColumn: {
            sortingField: "date",
            sortingComparator: (a: SearchHistoryItem, b: SearchHistoryItem) =>
              compareDates(a.date, b.date),
          },
          isDescending: true,
        },
      },
      selection: {},
    }
  );

  const updateFilter = <K extends keyof SearchHistoryFilters>(
    filter: K,
    value: SearchHistoryFilters[K]
  ) => {
    setFilters((previous) => ({ ...previous, [filter]: value }));
    actions.setCurrentPage(1);
  };

  const updateMarketplaces = (selectedOptions: readonly OptionDefinition[]) => {
    const hadAll = filters.marketplaces.some(
      (option) => option.value === DEFAULT_OPTION.value
    );
    const hasAll = selectedOptions.some(
      (option) => option.value === DEFAULT_OPTION.value
    );
    let marketplaces = [...selectedOptions];

    if (hasAll && !hadAll) {
      marketplaces = [DEFAULT_OPTION];
    } else if (hadAll) {
      marketplaces = [];
    }

    updateFilter("marketplaces", marketplaces);
  };

  const clearFilters = () => {
    setFilters(createDefaultSearchHistoryFilters());
    actions.setCurrentPage(1);
  };

  const deleteSelectedItems = () => {
    const selectedItems = collectionProps.selectedItems || [];
    const updatedItems = allItems.filter(
      (item) =>
        !selectedItems.some(
          (selected) =>
            selected.orderId === item.orderId && selected.stage === item.stage
        )
    );
    setAllItems(updatedItems);
    localStorage.setItem("orderIds", JSON.stringify(updatedItems));
  };

  return {
    items,
    collectionProps,
    paginationProps,
    deleteSelectedItems,
    clearFilters,
    filters,
    filterOptions: {
      stage: STAGE_FILTER_OPTIONS,
      gl: GLS,
      marketplaces: marketplaceOptions,
      status: STATUS_FILTER_OPTIONS,
    },
    updateFilter,
    updateMarketplaces,
  };
}
